import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, LogOut, CheckCircle, Archive, Plus, Trash2, Image as ImageIcon, MessageSquare, Clock, Filter, Sparkles, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingGallery, setLoadingGallery] = useState(true);

  // Active sub-view
  const [activeTab, setActiveTab] = useState("contacts"); // "contacts" | "gallery"
  const [contactFilter, setContactFilter] = useState("all"); // "all" | "pending" | "resolved" | "archived"

  // Form for adding image
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageCategory, setNewImageCategory] = useState("Nunți");
  const [addingImage, setAddingImage] = useState(false);

  const categories = [
    "Nunți",
    "Botezuri",
    "Aniversări",
    "Evenimente Corporate",
    "Decoruri",
    "Atmosferă și lumini"
  ];

  const fetchContacts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts`,
        { withCredentials: true }
      );
      setContacts(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Eroare la încărcarea solicitărilor.");
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/gallery`
      );
      setGallery(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Eroare la încărcarea galeriei.");
    } finally {
      setLoadingGallery(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchContacts();
      fetchGallery();
    }
  }, [user]);

  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/${id}/status`,
        { status },
        { withCredentials: true }
      );
      toast.success(response.data.message || `Solicitare mutată în ${status}.`);
      fetchContacts();
    } catch (err) {
      console.error(err);
      toast.error("Nu s-a putut actualiza starea solicitării.");
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!newImageUrl) {
      toast.error("Vă rugăm să introduceți URL-ul imaginii.");
      return;
    }
    setAddingImage(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`,
        { url: newImageUrl, category: newImageCategory },
        { withCredentials: true }
      );
      toast.success("Imagine adăugată cu succes în galerie!");
      setNewImageUrl("");
      fetchGallery();
    } catch (err) {
      console.error(err);
      toast.error("Eroare la adăugarea imaginii.");
    } finally {
      setAddingImage(false);
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți această imagine?")) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery/${id}`,
        { withCredentials: true }
      );
      toast.success(response.data.message || "Imagine ștearsă din galerie.");
      fetchGallery();
    } catch (err) {
      console.error(err);
      toast.error("Eroare la ștergerea imaginii.");
    }
  };

  // Filter contacts
  const filteredContacts = contactFilter === "all"
    ? contacts
    : contacts.filter(c => c.status === contactFilter);

  return (
    <div 
      data-testid="admin-dashboard-container"
      className="min-h-screen bg-[#0A0A0A] text-white font-sans flex flex-col"
    >
      {/* Header Panel */}
      <header className="bg-black/60 backdrop-blur-xl border-b border-white/10 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          
          {/* Logo / Admin info */}
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20">
              <Shield size={20} />
            </div>
            <div>
              <h1 className="font-serif-lux text-xl sm:text-2xl text-white tracking-wide">
                Control Room
              </h1>
              <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] uppercase">
                Elysium Events • {user?.name || "Admin"}
              </p>
            </div>
          </div>

          {/* Nav Links / Logout */}
          <div className="flex items-center space-x-4">
            <button
              data-testid="admin-dashboard-tab-contacts"
              onClick={() => setActiveTab("contacts")}
              className={`text-xs font-semibold tracking-widest uppercase py-3 px-5 border transition-all duration-300 ${
                activeTab === "contacts"
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "bg-transparent text-white/70 border-white/5 hover:border-white/20"
              }`}
            >
              Solicitări & Rezervări
            </button>

            <button
              data-testid="admin-dashboard-tab-gallery"
              onClick={() => setActiveTab("gallery")}
              className={`text-xs font-semibold tracking-widest uppercase py-3 px-5 border transition-all duration-300 ${
                activeTab === "gallery"
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "bg-transparent text-white/70 border-white/5 hover:border-white/20"
              }`}
            >
              Gestionare Galerie
            </button>

            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="p-3 bg-red-950/20 text-red-400 hover:text-white hover:bg-red-900 border border-red-900/30 transition-colors"
              title="Deconectare"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Panel Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Tab 1: Contacts Request List */}
        {activeTab === "contacts" && (
          <div className="space-y-6">
            
            {/* Filters bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-[#121212] border border-white/5">
              <div className="flex items-center space-x-2 text-sm text-white/60">
                <Filter size={16} className="text-[#D4AF37]" />
                <span className="font-semibold tracking-wide uppercase text-xs">Filtrare Stadiu:</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {["all", "pending", "resolved", "archived"].map((status) => (
                  <button
                    key={status}
                    data-testid={`admin-filter-btn-${status}`}
                    onClick={() => setContactFilter(status)}
                    className={`text-[10px] font-semibold tracking-widest uppercase px-4 py-2 border transition-all duration-300 ${
                      contactFilter === status
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "bg-transparent text-white/50 border-white/5 hover:text-white"
                    }`}
                  >
                    {status === "all" ? "Toate" : status === "pending" ? "În Așteptare" : status === "resolved" ? "Rezolvate" : "Arhivate"}
                  </button>
                ))}
              </div>
            </div>

            {/* Table / Requests List */}
            {loadingContacts ? (
              <div className="text-center py-20 animate-pulse text-white/50">
                Se încarcă solicitările...
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-20 bg-[#121212] border border-white/5 text-white/50">
                Nu există nicio solicitare înregistrată pentru acest filtru.
              </div>
            ) : (
              <div className="overflow-x-auto border border-white/10">
                <table 
                  data-testid="admin-contacts-table"
                  className="w-full text-left border-collapse"
                >
                  <thead>
                    <tr className="bg-black border-b border-white/10 text-xs tracking-widest uppercase text-white/60">
                      <th className="p-4 font-semibold">Client</th>
                      <th className="p-4 font-semibold">Contact</th>
                      <th className="p-4 font-semibold">Detalii Eveniment</th>
                      <th className="p-4 font-semibold">Mesaj</th>
                      <th className="p-4 font-semibold">Tip / Status</th>
                      <th className="p-4 font-semibold text-right">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {filteredContacts.map((contact) => (
                      <tr 
                        key={contact.id} 
                        data-testid={`admin-contact-row-${contact.id}`}
                        className="hover:bg-white/[0.02] text-sm font-light transition-colors"
                      >
                        {/* Client details */}
                        <td className="p-4">
                          <div className="font-semibold text-white">{contact.name}</div>
                          <div className="text-xs text-white/40 mt-0.5">
                            Adăugat la {new Date(contact.created_at).toLocaleDateString("ro-RO")}
                          </div>
                        </td>

                        {/* Contact info */}
                        <td className="p-4">
                          <div className="text-white/80">{contact.email}</div>
                          <div className="text-xs text-[#D4AF37] mt-0.5">{contact.phone}</div>
                        </td>

                        {/* Event Details */}
                        <td className="p-4">
                          {contact.type !== "contact" ? (
                            <div className="space-y-0.5">
                              <span className="bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] uppercase font-semibold px-2 py-0.5 border border-[#D4AF37]/20">
                                {contact.event_type}
                              </span>
                              <div className="text-xs text-white/80 pt-1">
                                Data: <strong className="font-medium">{contact.date}</strong>
                              </div>
                              <div className="text-xs text-white/60">
                                Invitați: <strong className="font-medium">{contact.guests}</strong>
                              </div>
                            </div>
                          ) : (
                            <span className="text-white/40 italic text-xs">Fără eveniment</span>
                          )}
                        </td>

                        {/* Message */}
                        <td className="p-4 max-w-xs">
                          <p className="text-xs text-white/70 line-clamp-3 leading-relaxed">
                            {contact.message}
                          </p>
                        </td>

                        {/* Type & Status */}
                        <td className="p-4">
                          <div className="space-y-2">
                            <span className="text-[10px] tracking-widest uppercase font-semibold text-white/50 block">
                              Tip: {contact.type}
                            </span>
                            <span className={`text-[10px] uppercase font-semibold px-2.5 py-1 ${
                              contact.status === "pending"
                                ? "bg-amber-950/40 text-amber-400 border border-amber-900/30"
                                : contact.status === "resolved"
                                ? "bg-green-950/40 text-green-400 border border-green-900/30"
                                : "bg-zinc-950/40 text-zinc-400 border border-zinc-900/30"
                            }`}>
                              {contact.status === "pending" ? "În Așteptare" : contact.status === "resolved" ? "Rezolvat" : "Arhivat"}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {contact.status !== "resolved" && (
                              <button
                                data-testid={`admin-resolve-btn-${contact.id}`}
                                onClick={() => handleUpdateStatus(contact.id, "resolved")}
                                className="p-2 bg-green-950/20 text-green-400 hover:text-white hover:bg-green-600 border border-green-900/30 transition-colors"
                                title="Marchează Rezolvat"
                              >
                                <CheckCircle size={14} />
                              </button>
                            )}

                            {contact.status !== "archived" && (
                              <button
                                data-testid={`admin-archive-btn-${contact.id}`}
                                onClick={() => handleUpdateStatus(contact.id, "archived")}
                                className="p-2 bg-zinc-950/20 text-zinc-400 hover:text-white hover:bg-zinc-700 border border-zinc-900/30 transition-colors"
                                title="Arhivează Solicitare"
                              >
                                <Archive size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Gallery Management */}
        {activeTab === "gallery" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Form to Add New Image */}
            <div className="lg:col-span-4 bg-[#121212] border border-[#D4AF37]/10 p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[3px] bg-[#D4AF37]" />
              
              <div className="flex items-center space-x-2 mb-6 text-[#D4AF37]">
                <Plus size={18} />
                <h3 className="font-serif-lux text-lg text-white font-medium">Adaugă Imagine Nouă</h3>
              </div>

              <form onSubmit={handleAddImage} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-wider text-white/50">URL Imagine Pexels/Unsplash</label>
                  <input
                    type="url"
                    required
                    data-testid="admin-gallery-url-input"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="https://images.pexels.com/..."
                    className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-xs focus:outline-none transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-wider text-white/50">Categorie</label>
                  <select
                    data-testid="admin-gallery-category-select"
                    value={newImageCategory}
                    onChange={(e) => setNewImageCategory(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-xs focus:outline-none transition-colors"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat} className="bg-[#121212]">{cat}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={addingImage}
                  data-testid="admin-gallery-submit-btn"
                  className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-3.5 transition-all duration-300"
                >
                  {addingImage ? "Se adaugă..." : "Adaugă în Galerie"}
                </button>
              </form>
            </div>

            {/* Gallery items grid */}
            <div className="lg:col-span-8 space-y-6">
              <h3 className="font-serif-lux text-xl text-white font-medium">Galeria Elysium ({gallery.length} imagini)</h3>
              
              {loadingGallery ? (
                <div className="text-center py-20 animate-pulse text-white/50">
                  Se încarcă elementele galeriei...
                </div>
              ) : gallery.length === 0 ? (
                <div className="text-center py-20 bg-[#121212] border border-white/5 text-white/50">
                  Nu există nicio imagine în galerie.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {gallery.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`admin-gallery-item-${item.id}`}
                      className="group relative h-48 border border-white/10 overflow-hidden bg-black"
                    >
                      <img
                        src={item.url}
                        alt={item.category}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />

                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-4 z-10">
                        <span className="bg-[#D4AF37] text-black text-[9px] uppercase font-semibold px-2 py-0.5 tracking-wider self-start">
                          {item.category}
                        </span>

                        <button
                          data-testid={`admin-delete-gallery-btn-${item.id}`}
                          onClick={() => handleDeleteImage(item.id)}
                          className="p-2 bg-red-950/80 text-red-400 hover:text-white hover:bg-red-600 self-end border border-red-900/30 transition-all duration-300"
                          title="Șterge Imaginea"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

      </main>
    </div>
  );
}
